import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { authAPI, isAuthenticationError } from "../services/api";
import { identifyUser, resetAnalytics, trackLoginSuccess, trackLogout } from "../services/analytics";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const initialCheckStartedRef = useRef(false);

  const retrySession = useCallback(async () => {
    const token = localStorage.getItem("reviewly_token");
    if (!token) {
      setUser(null);
      setSessionError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setSessionError(null);
    let lastError = null;

    // Brief outages and deployment restarts should not bounce a signed-in
    // user to the public site. Retry before presenting a recoverable error.
    for (const delayMs of [0, 400, 1200]) {
      if (delayMs) await new Promise((resolve) => setTimeout(resolve, delayMs));
      try {
        const res = await authAPI.getMe();
        if (res.success) {
          if (res.token) localStorage.setItem("reviewly_token", res.token);
          setUser(res.user);
          identifyUser(res.user);
          setLoading(false);
          return;
        }
      } catch (err) {
        lastError = err;
        if (isAuthenticationError(err)) {
          localStorage.removeItem("reviewly_token");
          setUser(null);
          setSessionError(null);
          setLoading(false);
          return;
        }
      }
    }

    console.error("Session verification temporarily unavailable:", lastError);
    setSessionError("We could not verify your session. Your login is still saved; check your connection and retry.");
    setLoading(false);
  }, []);

  // Check if user is logged in on mount
  useEffect(() => {
    if (initialCheckStartedRef.current) return;
    initialCheckStartedRef.current = true;
    retrySession();
  }, [retrySession]);

  const loginWithGoogle = useCallback(async (credential) => {
    const res = await authAPI.googleLogin(credential);
    if (res.success) {
      localStorage.setItem("reviewly_token", res.token);
      setUser(res.user);
      setSessionError(null);
      identifyUser(res.user);
      trackLoginSuccess(res.user);
    }
    return res;
  }, []);

  const loginWithGoogleCode = useCallback(async (code) => {
    const res = await authAPI.googleCodeLogin(code);
    if (res.success) {
      localStorage.setItem("reviewly_token", res.token);
      setUser(res.user);
      setSessionError(null);
      identifyUser(res.user);
      trackLoginSuccess(res.user);
    }
    return res;
  }, []);

  const logout = useCallback(async () => {
    trackLogout();
    try {
      await authAPI.logout();
    } catch (_) {}
    localStorage.removeItem("reviewly_token");
    setUser(null);
    setSessionError(null);
    resetAnalytics();
  }, []);

  const updateUser = useCallback(async (data) => {
    const res = await authAPI.updateMe(data);
    if (res.success) setUser(res.user);
    return res;
  }, []);

  const value = {
    user,
    loading,
    sessionError,
    isAuthenticated: !!user,
    loginWithGoogle,
    loginWithGoogleCode,
    logout,
    updateUser,
    retrySession,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
